import { Fragment, useState } from "react";
import { Card, EmptyState, Inline, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, UnauthenticatedError, apiGet } from "../lib/api.js";
import { useSession } from "../session/SessionProvider.js";
import { usePlatformOwner } from "../access/platform-owner.js";
import { LoadingSkeleton } from "../components/LoadingSkeleton.js";
import { type Locale } from "@genius-tracker/i18n";
import { formatDateTimeMediumShort } from "../locale-format.js";

/**
 * Sprint 25 Wave B PR-B3F1 — Admin Console email delivery log.
 *
 * Read-only platform-owner view over the email_delivery_log table
 * that PR-B0F1 created and PR-B2F1 wires the dispatcher to populate.
 * Lets the operator answer the customer-launch question "did
 * Alice's invitation actually leave the building?" without psql
 * access.
 *
 * Hard guarantees pinned at the implementation level:
 *   - Platform-owner gated. Non-platform-owners see the calm gate
 *     message, no fetch fires.
 *   - Provider message id is rendered as a TRUNCATED preview
 *     (already truncated server-side; the page just displays).
 *   - The redacted error code/message are length-bounded by the
 *     schema CHECKs and the writer; the page renders them as plain
 *     text inside a <code> tag so any HTML-looking content is
 *     escaped by React.
 *   - No raw provider payload, no token, no sender header beyond
 *     the saved sender_email column.
 */

const STATUSES = [
  "queued",
  "sent",
  "failed",
  "delivered",
  "bounced",
  "spam_complaint",
  "rejected",
] as const;
const PROVIDERS = ["log-only", "smtp", "postmark"] as const;
/** UI-TRUTH-01 — closed template-key filter set (0107 CHECK values). */
const TEMPLATE_KEYS = [
  "invitation",
  "report.ready",
  "alert.fired",
  "member.suspended",
  "admin.session.revoked",
] as const;
type Status = (typeof STATUSES)[number];
type Provider = (typeof PROVIDERS)[number];
type TemplateKey = (typeof TEMPLATE_KEYS)[number];

interface DeliveryRow {
  readonly id: string;
  readonly tenantId: string | null;
  readonly invitationId: string | null;
  readonly recipientEmail: string;
  readonly senderEmail: string;
  readonly templateKey: string;
  readonly provider: Provider;
  readonly providerMessageIdPreview: string | null;
  readonly status: Status;
  readonly redactedErrorCode: string | null;
  readonly redactedErrorMessage: string | null;
  readonly createdAt: string;
  readonly sentAt: string | null;
  readonly updatedAt: string | null;
  /**
   * UI-TRUTH-01 (v2 projection, migration 0110) — report correlation
   * (0109 intents) + lifecycle facts. All null for non-report rows
   * and rows written before 0109. `providerCallStartedAt` on a
   * `queued` row = the provider call STARTED and the outcome is
   * unknown (review!), which is a different truth than "waiting".
   * `providerEventAt` (0108) is the provider-reported event instant
   * behind delivered/bounced/spam_complaint.
   */
  readonly reportScheduleId: string | null;
  readonly reportOccurrenceId: string | null;
  readonly reportRunId: string | null;
  readonly reportScheduledFor: string | null;
  readonly providerCallStartedAt: string | null;
  readonly providerEventAt: string | null;
}

interface DeliveryListResponse {
  readonly items: ReadonlyArray<DeliveryRow>;
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

const PAGE_SIZE = 50;

/**
 * UI-TRUTH-01 CTO correction 3 — closed report-email error vocabulary
 * for the platform-owner diagnostic surface. Occurrence-level codes
 * (BUILD-01 coordinator, runner artifact path, recovery takeover, and
 * the defensively mapped run-level interruption markers) PLUS the
 * intent-level codes the delivery-log rows themselves carry
 * (`throttled`, `unknown_error`; provider error-CLASS names fall to
 * the generic explanation). Unknown codes render the localized
 * generic explanation while the raw stable code stays visible here —
 * this is the authorized Admin surface, unlike the tenant UI.
 */
const REPORT_ERROR_CODES: ReadonlyArray<string> = [
  "report_email_disabled",
  "no_recipients",
  "invalid_recipients",
  "too_many_recipients",
  "email_channel_refused",
  "email_channel_reserved",
  "email_channel_prohibited",
  "attachment_too_large",
  "provider_outcome_ambiguous",
  "occurrence_claim_lost",
  "occurrence_claim_lost_after_delivery",
  "occurrence_finalization_unknown",
  "coordinator_error",
  "artifact_serialization_failed",
  "generation_lease_expired",
  "delivery_interrupted",
  "artifact_ready_stalled",
  "claim_attempts_exhausted",
  "schedule_deleted",
  "schedule_inactive",
  "claim_lost",
  "claim_lost_after_generation",
  "throttled",
  "unknown_error",
];

export function EmailDeliveryPage() {
  const { t } = useLocale();
  const session = useSession();

  if (!usePlatformOwner(session)) {
    return (
      <Card elevation={1}>
        <EmptyState
          title={t("admin.email_delivery.gate.title")}
          body={t("admin.email_delivery.gate.body")}
        />
      </Card>
    );
  }

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h1
          style={{
            fontSize: "var(--gt-sys-font-size-2xl)",
            fontWeight: "var(--gt-sys-font-weight-bold)",
            margin: 0,
          }}
        >
          {t("admin.email_delivery.title")}
        </h1>
        <p style={{ color: "var(--gt-sys-text-muted)", margin: 0 }}>
          {t("admin.email_delivery.subtitle")}
        </p>
        <p
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
            margin: 0,
            paddingBlockStart: "var(--gt-sys-space-1)",
          }}
        >
          {t("admin.email_delivery.read_only_hint")}
        </p>
        {/* GT-APPLICATION-OUTBOUND-EMAIL-PHASE-1A — status truth. The
            table's "sent" means provider acceptance at most; log-only
            rows never left the system; delivered/bounced/spam are
            reserved statuses no code path currently produces. */}
        <p
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
            margin: 0,
            paddingBlockStart: "var(--gt-sys-space-1)",
          }}
          data-testid="admin-email-delivery-status-truth"
        >
          {t("admin.email_delivery.status_truth_note")}
        </p>
      </Stack>
      <EmailDeliveryContent />
    </Stack>
  );
}

interface FilterState {
  status: Status | "";
  provider: Provider | "";
  recipient: string;
  // UI-TRUTH-01 — v2 filters: closed template key + report-schedule
  // correlation (set via the per-row "filter by this schedule" action).
  templateKey: TemplateKey | "";
  reportScheduleId: string;
  offset: number;
}

/**
 * Shared delivery-log surface used by gtcc1 and the GTCC mono-deck.
 *
 * Both shells own their platform-owner gate and page heading. Keeping the
 * filters, lifecycle truth, report correlation and pagination in one component
 * prevents the two operator entry points from drifting.
 */
export function EmailDeliveryContent({
  queryScope = "admin",
}: {
  readonly queryScope?: "admin" | "gtcc";
} = {}) {
  const { t, locale } = useLocale();
  const [filter, setFilter] = useState<FilterState>({
    status: "",
    provider: "",
    recipient: "",
    templateKey: "",
    reportScheduleId: "",
    offset: 0,
  });
  const [recipientInput, setRecipientInput] = useState("");

  const params: Record<string, string> = {
    limit: String(PAGE_SIZE),
    offset: String(filter.offset),
  };
  if (filter.status) params["status"] = filter.status;
  if (filter.provider) params["provider"] = filter.provider;
  if (filter.recipient) params["recipient"] = filter.recipient;
  if (filter.templateKey) params["templateKey"] = filter.templateKey;
  if (filter.reportScheduleId) params["reportScheduleId"] = filter.reportScheduleId;

  const hasExtendedGtccFilter = Boolean(
    filter.provider || filter.recipient || filter.templateKey || filter.reportScheduleId,
  );
  const queryKey =
    queryScope === "admin"
      ? (["admin", "email-delivery", filter] as const)
      : hasExtendedGtccFilter
        ? (["gtcc", "email-deliveries", filter] as const)
        : filter.status
          ? (["gtcc", "email-deliveries", filter.status, filter.offset] as const)
          : (["gtcc", "email-deliveries", filter.offset] as const);

  const query = useQuery<DeliveryListResponse, Error>({
    queryKey,
    queryFn: ({ signal }) =>
      apiGet<DeliveryListResponse>("/v1/admin/email-deliveries", params, signal),
    staleTime: 15_000,
  });

  function applyFilters(patch: Partial<FilterState>) {
    setFilter((prev) => ({ ...prev, ...patch, offset: 0 }));
  }

  function applyOffset(nextOffset: number) {
    setFilter((prev) => ({ ...prev, offset: Math.max(0, nextOffset) }));
  }

  return (
    <Stack gap={3} data-testid="admin-email-delivery-content">
      <Card elevation={1}>
        <Stack gap={2}>
          <strong>{t("admin.email_delivery.filters.title")}</strong>
          <Inline gap={3} align="end" wrap>
            <FilterField label={t("admin.email_delivery.filters.status")}>
              <select
                data-testid="admin-email-delivery-filter-status"
                value={filter.status}
                onChange={(e) => applyFilters({ status: e.currentTarget.value as Status | "" })}
                style={selectStyle}
              >
                <option value="">{t("admin.email_delivery.filters.any")}</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.email_delivery.status.${s}` as const)}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField label={t("admin.email_delivery.filters.provider")}>
              <select
                data-testid="admin-email-delivery-filter-provider"
                value={filter.provider}
                onChange={(e) => applyFilters({ provider: e.currentTarget.value as Provider | "" })}
                style={selectStyle}
              >
                <option value="">{t("admin.email_delivery.filters.any")}</option>
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {t(`admin.email_delivery.provider.${p}` as const)}
                  </option>
                ))}
              </select>
            </FilterField>
            {/* UI-TRUTH-01 — template-key filter (closed set; raw keys
                shown verbatim — they are the wire vocabulary). */}
            <FilterField label={t("admin.email_delivery.filters.template")}>
              <select
                data-testid="admin-email-delivery-filter-template"
                value={filter.templateKey}
                onChange={(e) =>
                  applyFilters({ templateKey: e.currentTarget.value as TemplateKey | "" })
                }
                style={selectStyle}
              >
                <option value="">{t("admin.email_delivery.filters.any")}</option>
                {TEMPLATE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField label={t("admin.email_delivery.filters.recipient")}>
              <Inline gap={1} align="center">
                <input
                  data-testid="admin-email-delivery-filter-recipient"
                  type="search"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyFilters({ recipient: recipientInput.trim() });
                  }}
                  placeholder={t("admin.email_delivery.filters.recipient_placeholder")}
                  style={textInputStyle}
                  maxLength={254}
                />
                <button
                  type="button"
                  onClick={() => applyFilters({ recipient: recipientInput.trim() })}
                  data-testid="admin-email-delivery-filter-recipient-apply"
                  style={ghostButtonStyle}
                >
                  {t("admin.email_delivery.filters.apply")}
                </button>
                {(filter.recipient ||
                  filter.status ||
                  filter.provider ||
                  filter.templateKey ||
                  filter.reportScheduleId) && (
                  <button
                    type="button"
                    onClick={() => {
                      setRecipientInput("");
                      setFilter({
                        status: "",
                        provider: "",
                        recipient: "",
                        templateKey: "",
                        reportScheduleId: "",
                        offset: 0,
                      });
                    }}
                    data-testid="admin-email-delivery-filter-clear"
                    style={ghostButtonStyle}
                  >
                    {t("admin.email_delivery.filters.clear")}
                  </button>
                )}
              </Inline>
            </FilterField>
          </Inline>
          {filter.reportScheduleId ? (
            <Inline gap={2} align="center" data-testid="admin-email-delivery-schedule-filter-note">
              <span
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontSize: "var(--gt-sys-font-size-xs)",
                }}
              >
                {t("admin.email_delivery.filters.schedule_active", {
                  id: filter.reportScheduleId,
                })}
              </span>
              <button
                type="button"
                onClick={() => applyFilters({ reportScheduleId: "" })}
                data-testid="admin-email-delivery-schedule-filter-clear"
                style={ghostButtonStyle}
              >
                {t("admin.email_delivery.filters.clear")}
              </button>
            </Inline>
          ) : null}
        </Stack>
      </Card>

      {query.isLoading ? (
        <LoadingSkeleton testId="admin-email-delivery-loading" />
      ) : query.isError ? (
        <Card elevation={1}>
          <Stack gap={1} data-testid="admin-email-delivery-error">
            <strong role="alert" style={{ color: "var(--gt-sys-danger)" }}>
              {query.error instanceof UnauthenticatedError
                ? t("admin.email_delivery.error.session_expired_title")
                : query.error instanceof ApiError && query.error.status === 403
                  ? t("admin.email_delivery.error.forbidden_title")
                  : t("admin.email_delivery.error.load_title")}
            </strong>
            <span
              style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-sm)" }}
            >
              {query.error instanceof UnauthenticatedError
                ? t("admin.email_delivery.error.session_expired_body")
                : query.error instanceof ApiError && query.error.status === 403
                  ? t("admin.email_delivery.error.forbidden_body")
                  : t("admin.email_delivery.error.load_body")}
            </span>
          </Stack>
        </Card>
      ) : query.data && query.data.items.length === 0 ? (
        <Card elevation={1}>
          <EmptyState
            title={t("admin.email_delivery.empty.title")}
            body={t("admin.email_delivery.empty.body")}
          />
        </Card>
      ) : query.data ? (
        <Stack gap={2}>
          {/* Sprint 34 (GT-COMMS-DOMAINS-FOUNDATION-01) — at-a-glance
              status counts for the current visible page so operators
              can see the failure mix without scanning every row.
              Counts are computed client-side from `items` so no new
              API query is added (keeps the Sprint 27.2 sequential
              query invariant intact). */}
          <StatusSummaryRow rows={query.data.items} />
          <DeliveryTable
            rows={query.data.items}
            locale={locale}
            onFilterSchedule={(scheduleId) => applyFilters({ reportScheduleId: scheduleId })}
          />
          <Inline justify="space-between" align="center">
            <span
              style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-sm)" }}
              data-testid="admin-email-delivery-total"
            >
              {t("admin.email_delivery.total", { total: query.data.total })}
            </span>
            <Inline gap={1} align="center">
              <button
                type="button"
                onClick={() => applyOffset(filter.offset - PAGE_SIZE)}
                disabled={filter.offset === 0}
                data-testid="admin-email-delivery-prev"
                style={ghostButtonStyle}
              >
                {t("admin.email_delivery.pagination.prev")}
              </button>
              <button
                type="button"
                onClick={() => applyOffset(filter.offset + PAGE_SIZE)}
                disabled={filter.offset + PAGE_SIZE >= query.data.total}
                data-testid="admin-email-delivery-next"
                style={ghostButtonStyle}
              >
                {t("admin.email_delivery.pagination.next")}
              </button>
            </Inline>
          </Inline>
        </Stack>
      ) : null}
    </Stack>
  );
}

function StatusSummaryRow({ rows }: { rows: ReadonlyArray<DeliveryRow> }) {
  const { t } = useLocale();
  const counts: Record<Status, number> = {
    queued: 0,
    sent: 0,
    failed: 0,
    delivered: 0,
    bounced: 0,
    spam_complaint: 0,
    rejected: 0,
  };
  for (const r of rows) {
    counts[r.status] = (counts[r.status] ?? 0) + 1;
  }
  const ordered: Status[] = [
    "queued",
    "sent",
    "delivered",
    "failed",
    "bounced",
    "spam_complaint",
    "rejected",
  ];
  return (
    <Card elevation={0}>
      <Inline gap={2} wrap data-testid="admin-email-delivery-status-summary">
        {ordered.map((s) => (
          <span
            key={s}
            data-testid={`admin-email-delivery-status-summary-${s}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              border: "1px solid var(--gt-sys-border)",
              background: counts[s] > 0 ? "var(--gt-sys-surface)" : "transparent",
              fontSize: "var(--gt-sys-font-size-xs)",
              color:
                counts[s] === 0
                  ? "var(--gt-sys-text-muted)"
                  : s === "failed" || s === "bounced" || s === "spam_complaint"
                    ? "var(--gt-sys-danger)"
                    : "var(--gt-sys-text)",
            }}
          >
            <span style={{ fontWeight: "var(--gt-sys-font-weight-semibold)" }}>{counts[s]}</span>
            <span>{t(`admin.email_delivery.status.${s}` as const)}</span>
          </span>
        ))}
      </Inline>
    </Card>
  );
}

function DeliveryTable(props: {
  rows: ReadonlyArray<DeliveryRow>;
  locale: Locale;
  onFilterSchedule: (scheduleId: string) => void;
}) {
  const { t } = useLocale();
  return (
    <Card elevation={1}>
      <div style={{ overflowX: "auto" }}>
        <table
          data-testid="admin-email-delivery-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "var(--gt-sys-font-size-sm)",
          }}
          aria-label={t("admin.email_delivery.table.title")}
        >
          <thead>
            <tr>
              <Th>{t("admin.email_delivery.col.created")}</Th>
              <Th>{t("admin.email_delivery.col.recipient")}</Th>
              <Th>{t("admin.email_delivery.col.template")}</Th>
              <Th>{t("admin.email_delivery.col.provider")}</Th>
              <Th>{t("admin.email_delivery.col.status")}</Th>
              <Th>{t("admin.email_delivery.col.report")}</Th>
              <Th>{t("admin.email_delivery.col.message_id")}</Th>
              <Th>{t("admin.email_delivery.col.error")}</Th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <tr
                key={row.id}
                data-testid={`admin-email-delivery-row-${row.id}`}
                style={{ borderBlockStart: "1px solid var(--gt-sys-border)" }}
              >
                <Td>
                  <time dateTime={row.createdAt}>
                    {formatDateTimeMediumShort(row.createdAt, props.locale)}
                  </time>
                </Td>
                <Td>
                  <span style={{ wordBreak: "break-all" }}>{row.recipientEmail}</span>
                </Td>
                <Td>
                  <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>{row.templateKey}</code>
                </Td>
                <Td>
                  <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>{row.provider}</code>
                </Td>
                <Td>
                  {/* UI-TRUTH-01 — lifecycle-aware status truth. */}
                  <Stack gap={0}>
                    <StatusChip
                      status={row.status}
                      providerCallStarted={row.providerCallStartedAt !== null}
                    />
                    {row.providerEventAt ? (
                      <span
                        style={{
                          fontSize: "var(--gt-sys-font-size-xs)",
                          color: "var(--gt-sys-text-muted)",
                        }}
                        data-testid="admin-email-delivery-provider-event-at"
                      >
                        {t("admin.email_delivery.provider_event_at", {
                          time: formatDateTimeMediumShort(row.providerEventAt, props.locale),
                        })}
                      </span>
                    ) : null}
                  </Stack>
                </Td>
                <Td>
                  {/* UI-TRUTH-01 — report correlation (0109/0110). */}
                  {row.reportScheduleId ? (
                    <Stack gap={0} data-testid="admin-email-delivery-report-cell">
                      <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>
                        {row.reportScheduleId.slice(0, 8)}…
                      </code>
                      {row.reportScheduledFor ? (
                        <span
                          style={{
                            fontSize: "var(--gt-sys-font-size-xs)",
                            color: "var(--gt-sys-text-muted)",
                          }}
                        >
                          {t("admin.email_delivery.report.scheduled_for", {
                            time: formatDateTimeMediumShort(row.reportScheduledFor, props.locale),
                          })}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => props.onFilterSchedule(row.reportScheduleId ?? "")}
                        data-testid="admin-email-delivery-report-filter-by"
                        style={{
                          ...ghostButtonStyle,
                          paddingBlock: 0,
                          paddingInline: "var(--gt-sys-space-2)",
                          fontSize: "var(--gt-sys-font-size-xs)",
                          alignSelf: "start",
                        }}
                      >
                        {t("admin.email_delivery.report.filter_by")}
                      </button>
                      <ReportTechnicalDetails row={row} locale={props.locale} />
                    </Stack>
                  ) : (
                    <span style={{ color: "var(--gt-sys-text-muted)" }}>—</span>
                  )}
                </Td>
                <Td>
                  {row.providerMessageIdPreview ? (
                    <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>
                      {row.providerMessageIdPreview}
                    </code>
                  ) : (
                    <span style={{ color: "var(--gt-sys-text-muted)" }}>—</span>
                  )}
                </Td>
                <Td>
                  {row.redactedErrorCode || row.redactedErrorMessage ? (
                    <Stack gap={0}>
                      {row.redactedErrorCode ? (
                        <code
                          style={{
                            fontSize: "var(--gt-sys-font-size-xs)",
                            color: "var(--gt-sys-danger)",
                          }}
                        >
                          {row.redactedErrorCode}
                        </code>
                      ) : null}
                      {row.redactedErrorMessage ? (
                        <span
                          style={{
                            fontSize: "var(--gt-sys-font-size-xs)",
                            color: "var(--gt-sys-text-muted)",
                          }}
                        >
                          {row.redactedErrorMessage}
                        </span>
                      ) : null}
                    </Stack>
                  ) : (
                    <span style={{ color: "var(--gt-sys-text-muted)" }}>—</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * CTO correction 3 — accessible expandable technical details for a
 * report-email row (native <details>/<summary>: keyboard-operable,
 * screen-reader announced, no JS state). Platform-owner surface ONLY
 * — the tenant Web UI never receives these identifiers.
 *
 * Shows the complete safe correlation: template key, FULL schedule /
 * occurrence / run IDs (0109), scheduled occurrence instant,
 * provider-call-started instant (why a queued row is "outcome
 * unknown"), provider event instant (0108 webhook evidence),
 * provider, truncated MessageID preview, lifecycle status, the raw
 * stable redacted error code AND its localized report-error
 * explanation (generic explanation for unknown codes — the raw code
 * stays visible here because this is the diagnostic surface).
 *
 * Never shown: attachment bytes, report rows, provider payloads,
 * tokens, unredacted provider errors, secret-derived configuration —
 * none of those exist in the v2 DTO to begin with.
 */
function ReportTechnicalDetails({ row, locale }: { row: DeliveryRow; locale: Locale }) {
  const { t } = useLocale();
  const na = t("admin.email_delivery.details.not_available");
  const fmt = (iso: string | null): string => (iso ? formatDateTimeMediumShort(iso, locale) : na);
  const lifecycle =
    row.status === "queued" && row.providerCallStartedAt !== null
      ? t("admin.email_delivery.status.queued_call_started")
      : t(`admin.email_delivery.status.${row.status}` as const);
  const errorExplanation = row.redactedErrorCode
    ? REPORT_ERROR_CODES.includes(row.redactedErrorCode)
      ? t(`admin.email_delivery.report_error.${row.redactedErrorCode}`)
      : t("admin.email_delivery.report_error.generic")
    : null;
  const fields: ReadonlyArray<{ key: string; label: string; value: React.ReactNode }> = [
    {
      key: "template",
      label: t("admin.email_delivery.details.template"),
      value: <IdCode value={row.templateKey} />,
    },
    {
      key: "schedule-id",
      label: t("admin.email_delivery.details.schedule_id"),
      value: <IdCode value={row.reportScheduleId ?? na} />,
    },
    {
      key: "occurrence-id",
      label: t("admin.email_delivery.details.occurrence_id"),
      value: <IdCode value={row.reportOccurrenceId ?? na} />,
    },
    {
      key: "run-id",
      label: t("admin.email_delivery.details.run_id"),
      value: <IdCode value={row.reportRunId ?? na} />,
    },
    {
      key: "scheduled-for",
      label: t("admin.email_delivery.details.scheduled_for"),
      value: fmt(row.reportScheduledFor),
    },
    {
      key: "provider-call-started",
      label: t("admin.email_delivery.details.provider_call_started"),
      value: fmt(row.providerCallStartedAt),
    },
    {
      key: "provider-event",
      label: t("admin.email_delivery.details.provider_event"),
      value: fmt(row.providerEventAt),
    },
    {
      key: "provider",
      label: t("admin.email_delivery.details.provider"),
      value: <IdCode value={row.provider} />,
    },
    {
      key: "message-id",
      label: t("admin.email_delivery.details.message_id_preview"),
      value: row.providerMessageIdPreview ? <IdCode value={row.providerMessageIdPreview} /> : na,
    },
    { key: "lifecycle", label: t("admin.email_delivery.details.lifecycle"), value: lifecycle },
  ];
  return (
    <details data-testid="admin-email-delivery-report-details" style={{ maxInlineSize: 340 }}>
      <summary
        style={{ cursor: "pointer", fontSize: "var(--gt-sys-font-size-xs)" }}
        data-testid="admin-email-delivery-report-details-summary"
      >
        {t("admin.email_delivery.details.summary")}
      </summary>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          gap: "2px var(--gt-sys-space-2)",
          margin: 0,
          paddingBlockStart: "var(--gt-sys-space-1)",
          fontSize: "var(--gt-sys-font-size-xs)",
        }}
      >
        {fields.map((f) => (
          <Fragment key={f.key}>
            <dt style={{ color: "var(--gt-sys-text-muted)" }}>{f.label}</dt>
            <dd
              style={{ margin: 0, overflowWrap: "anywhere" }}
              data-testid={`admin-email-delivery-detail-${f.key}`}
            >
              {f.value}
            </dd>
          </Fragment>
        ))}
        {row.redactedErrorCode ? (
          <>
            <dt style={{ color: "var(--gt-sys-text-muted)" }}>
              {t("admin.email_delivery.details.error_code")}
            </dt>
            <dd
              style={{ margin: 0, overflowWrap: "anywhere" }}
              data-testid="admin-email-delivery-detail-error-code"
            >
              <IdCode value={row.redactedErrorCode} />
            </dd>
            <dt style={{ color: "var(--gt-sys-text-muted)" }}>
              {t("admin.email_delivery.details.error_explanation")}
            </dt>
            <dd
              style={{ margin: 0, overflowWrap: "anywhere" }}
              data-testid="admin-email-delivery-detail-error-explanation"
            >
              {errorExplanation}
            </dd>
          </>
        ) : null}
      </dl>
    </details>
  );
}

/** Copy-friendly identifier: wraps safely, never overflows the row. */
function IdCode({ value }: { value: string }) {
  return (
    <code style={{ fontSize: "var(--gt-sys-font-size-xs)", overflowWrap: "anywhere" }}>
      {value}
    </code>
  );
}

/**
 * UI-TRUTH-01 — lifecycle-aware status chip.
 *
 * Truth vocabulary (never collapsed):
 *   - `queued` WITHOUT a provider-call start = waiting (pending).
 *   - `queued` WITH `provider_call_started_at` set = the provider
 *     call STARTED and no outcome was recorded — "outcome unknown",
 *     review territory. Rendering this as plain "queued" would hide
 *     a possibly-sent message.
 *   - `sent` = provider ACCEPTED (accent tint, not the success
 *     green) — only `delivered` (0108 webhook evidence) is green.
 */
function StatusChip({
  status,
  providerCallStarted = false,
}: {
  status: Status;
  providerCallStarted?: boolean;
}) {
  const { t } = useLocale();
  const palette: Record<Status, string> = {
    sent: "color-mix(in oklab, var(--gt-sys-accent, var(--gt-sys-text-muted)) 18%, transparent)",
    delivered:
      "color-mix(in oklab, var(--gt-sys-success, var(--gt-sys-text-muted)) 22%, transparent)",
    queued: "color-mix(in oklab, var(--gt-sys-warning, var(--gt-sys-text-muted)) 18%, transparent)",
    failed: "color-mix(in oklab, var(--gt-sys-danger) 22%, transparent)",
    bounced: "color-mix(in oklab, var(--gt-sys-danger) 22%, transparent)",
    spam_complaint: "color-mix(in oklab, var(--gt-sys-danger) 22%, transparent)",
    rejected:
      "color-mix(in oklab, var(--gt-sys-warning, var(--gt-sys-text-muted)) 22%, transparent)",
  };
  const callStartedUnknown = status === "queued" && providerCallStarted;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "var(--gt-sys-font-size-xs)",
        fontWeight: "var(--gt-sys-font-weight-semibold)",
        background: callStartedUnknown
          ? "color-mix(in oklab, var(--gt-sys-danger) 18%, transparent)"
          : palette[status],
      }}
      data-testid={callStartedUnknown ? "admin-email-delivery-status-call-started" : undefined}
    >
      {callStartedUnknown
        ? t("admin.email_delivery.status.queued_call_started")
        : t(`admin.email_delivery.status.${status}` as const)}
    </span>
  );
}

function FilterField(props: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        style={{
          fontSize: "var(--gt-sys-font-size-xs)",
          color: "var(--gt-sys-text-muted)",
          fontWeight: "var(--gt-sys-font-weight-semibold)",
          textTransform: "uppercase",
          letterSpacing: 0.04,
        }}
      >
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      style={{
        textAlign: "start",
        padding: "var(--gt-sys-space-2)",
        color: "var(--gt-sys-text-muted)",
        fontWeight: "var(--gt-sys-font-weight-medium)",
        fontSize: "var(--gt-sys-font-size-xs)",
        textTransform: "uppercase",
        letterSpacing: 0.04,
        borderBlockEnd: "1px solid var(--gt-sys-border)",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: "var(--gt-sys-space-2)", verticalAlign: "top" }}>{children}</td>;
}

const selectStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: "var(--gt-sys-radius-sm)",
  border: "1px solid var(--gt-sys-border)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  font: "inherit",
  minInlineSize: 160,
};

const textInputStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: "var(--gt-sys-radius-sm)",
  border: "1px solid var(--gt-sys-border)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  font: "inherit",
  minInlineSize: 220,
};

const ghostButtonStyle: React.CSSProperties = {
  appearance: "none",
  border: "1px solid var(--gt-sys-border)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-3)",
  borderRadius: "var(--gt-sys-radius-sm)",
  font: "inherit",
  cursor: "pointer",
};
